import { FdrCrossSellHeader, NestedLink } from "@fanduel/og-fe-library";
import DOMPurify from "isomorphic-dompurify";
import { useMemo, useRef } from "react";
import { GetMenusQuery } from "@/graphql/gql/graphql";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import { useNavMenus } from "@/utils/hooks/useMenus";
import { filterEmpty } from "@/utils/index";
import { getSiteNavItems } from "../../../utils/getSiteNavItems";
import MainNavigation from "../MainNavigation/MainNavigation";
import MobileContainer from "../MobileContainer/MobileContainer";
import Styles from "./HeaderContainer.module.scss";

interface HeaderContainerProps {
  sport?: string;
  sportSlug?: string;
  initialData?: GetMenusQuery;
}

const HeaderContainer = ({
  sport,
  sportSlug,
  initialData,
}: HeaderContainerProps) => {
  const { menuData, isLoading } = useNavMenus(initialData);
  const { sessionId, deviceId } = useAmplitude();
  const hasRun = useRef(false);

  const crossSellMenu = useMemo(() => {
    if (!hasRun.current && sessionId && deviceId) {
      hasRun.current = true;
      const menuItemsData: NestedLink[] = menuData.header?.menuItems
        ? filterEmpty(menuData.header.menuItems)
        : [];
      return menuItemsData;
    }
    return filterEmpty(menuData.header?.menuItems ?? []);
  }, [sessionId, deviceId, menuData.header]);

  const contentNav = menuData?.["Research Menu"];
  const mobile = menuData?.["Research Mobile"];

  if (isLoading) {
    return null;
  }
  const siteNavItems = getSiteNavItems(contentNav?.menuItems || []);
  const siteNavJsonLd = {
    "@context": "https://schema.org",
    "@graph": siteNavItems.map((item) => ({
      "@type": "SiteNavigationElement",
      name: item.name,
      url: item.url,
    })),
  };
  const safeSiteNavJsonLd = DOMPurify.sanitize(JSON.stringify(siteNavJsonLd));

  return (
    <>
      {contentNav && (
        <script
          id="site-navigation-schema"
          type="application/ld+json"
          data-testid="site-navigation-schema"
          dangerouslySetInnerHTML={{ __html: safeSiteNavJsonLd }}
        />
      )}
      <div className={Styles.placeholder} />
      <div className={Styles.container}>
        <div className={Styles.scrollContainer}>
          <FdrCrossSellHeader menuItems={crossSellMenu} />
        </div>
        <div className={Styles.researchNav}>
          <MainNavigation
            sport={sport}
            sportSlug={sportSlug}
            menuItems={contentNav?.menuItems ?? []}
          />
          <MobileContainer menuItems={mobile?.menuItems ?? []} />
        </div>
      </div>
    </>
  );
};
export default HeaderContainer;
